import React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CircleAlertIcon,
  CircleCheckIcon,
  ClockIcon,
  Loader2Icon,
  RotateCwIcon,
} from "lucide-react";
import { useRootAPI } from "@/api";
import { Button } from "@/components/ui/button";
import { cn } from "@/utils/cn";
import { useT } from "@/i18n";

type Props = {
  businessId?: string;
  userIsDemo: boolean;
  inactiveTab: boolean;
};

export function OrganizationSyncControl({ businessId, userIsDemo, inactiveTab }: Props) {
  const api = useRootAPI();
  const queryClient = useQueryClient();
  const { t, tn, locale, formatNumber } = useT();
  const [now, setNow] = React.useState(Date.now());
  const [startError, setStartError] = React.useState<{ lastFinishedJobId?: number } | null>(null);
  const seenStatus = React.useRef(false);
  const lastFinishedJobId = React.useRef<number | undefined>(undefined);

  React.useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  const statusQuery = useQuery({
    queryKey: ["organization-users-sync"],
    queryFn: () => api.getOrganizationUsersSync(),
    enabled: !userIsDemo,
    refetchInterval: 5000,
    meta: { preventDefaultErrorHandling: true },
  });

  const start = useMutation({
    mutationFn: () => api.postOrganizationUsersSync(),
    onMutate: () => setStartError(null),
    onSuccess: (status) => {
      queryClient.setQueryData(["organization-users-sync"], status);
    },
    onError: () => setStartError({ lastFinishedJobId: statusQuery.data?.last_finished_job_id }),
    meta: { preventDefaultErrorHandling: true },
  });

  React.useEffect(() => {
    if (
      startError &&
      statusQuery.isSuccess &&
      (statusQuery.data.active_source === "manual" ||
        statusQuery.data.manual_sync_queued ||
        (statusQuery.data.last_result_source === "manual" &&
          statusQuery.data.last_finished_job_id !== startError.lastFinishedJobId))
    ) {
      setStartError(null);
    }
  }, [startError, statusQuery.isSuccess, statusQuery.data]);

  const rejectedStart = start.data?.manual_sync_started === false ? start.data : undefined;
  const resetStart = start.reset;
  React.useEffect(() => {
    if (!rejectedStart || !statusQuery.data) return;
    if (
      statusQuery.data.status !== rejectedStart.status ||
      statusQuery.data.job_id !== rejectedStart.job_id ||
      statusQuery.data.last_finished_job_id !== rejectedStart.last_finished_job_id
    ) {
      resetStart();
    }
  }, [rejectedStart, statusQuery.data, resetStart]);

  React.useEffect(() => {
    if (!statusQuery.data) return;
    const finishedID = statusQuery.data.last_finished_job_id;
    if (
      seenStatus.current &&
      finishedID !== undefined &&
      finishedID !== lastFinishedJobId.current
    ) {
      if (businessId) {
        const key = inactiveTab
          ? ["accounts", businessId, userIsDemo]
          : ["users", businessId, userIsDemo];
        void queryClient.invalidateQueries({ queryKey: key });
      }
    }
    seenStatus.current = true;
    lastFinishedJobId.current = finishedID;
  }, [statusQuery.data, businessId, inactiveTab, userIsDemo, queryClient]);

  if (userIsDemo) return null;

  const status = statusQuery.data;
  const waitMs = status?.next_allowed_manual_sync_at
    ? Math.max(0, new Date(status.next_allowed_manual_sync_at).getTime() - now)
    : 0;
  const waitMinutes = Math.ceil(waitMs / 60000);
  const lastSyncFailed = status?.last_result === "failed";
  const visibleFailure = status?.status === "idle" && lastSyncFailed;
  const manualStartRejected = start.data?.manual_sync_started === false;
  const rejectedLabel =
    status?.status === "running"
      ? t("sync.anotherStarted")
      : status?.active_source === "manual"
        ? t("sync.alreadyQueued")
        : waitMs > 0
          ? tn("sync.availableIn", waitMinutes)
          : t("sync.didNotStart");
  const retryAt = status?.next_retry_at
    ? new Date(status.next_retry_at).toLocaleString(locale)
    : undefined;
  const lastStartedAt = status?.next_allowed_manual_sync_at
    ? new Date(new Date(status.next_allowed_manual_sync_at).getTime() - 15 * 60_000)
    : undefined;
  const lastRunLabel = lastStartedAt
    ? lastStartedAt.toDateString() === new Date(now).toDateString()
      ? lastStartedAt.toLocaleTimeString(locale, { hour: "numeric", minute: "2-digit" })
      : lastStartedAt.toLocaleDateString(locale)
    : undefined;
  const label = statusQuery.isPending
    ? t("sync.checking")
    : statusQuery.isError
      ? t("sync.unavailable")
      : status?.status === "running"
        ? status.manual_sync_queued
          ? t("sync.refreshingQueued")
          : t("sync.refreshing")
        : status?.status === "queued"
          ? status.manual_sync_queued
            ? t("sync.yourRefreshQueued")
            : retryAt
              ? t("sync.automaticRetry", { time: retryAt })
              : t("sync.queued")
          : visibleFailure
            ? lastRunLabel
              ? t("sync.failedAt", { time: lastRunLabel })
              : t("sync.lastFailed")
            : lastRunLabel
              ? t("sync.lastRun", { time: lastRunLabel })
              : t("sync.available");
  const statusColor =
    statusQuery.isError || visibleFailure
      ? "text-destructive"
      : status?.status === "idle" && status.last_result === "completed"
        ? "text-success"
        : "text-primary";
  const cooldownProgress = Math.min(1, Math.max(0, 1 - waitMs / (15 * 60_000)));
  const canRefresh = !!status?.can_start_manually && !start.isPending && waitMs === 0;

  const StatusIcon =
    status?.status === "running"
      ? Loader2Icon
      : status?.status === "queued"
        ? ClockIcon
        : lastSyncFailed
          ? CircleAlertIcon
          : CircleCheckIcon;

  return (
    <div
      className="flex min-w-0 flex-wrap items-center justify-end gap-x-2 gap-y-1 text-sm"
      role="status"
      aria-live="polite"
    >
      <StatusIcon
        aria-hidden="true"
        className={cn(
          "size-4 shrink-0",
          statusColor,
          status?.status === "running" && "animate-spin",
        )}
      />
      <span
        className={
          statusQuery.isError || visibleFailure ? "text-destructive" : "text-muted-foreground"
        }
        title={
          lastStartedAt
            ? t("sync.lastRunTitle", { time: lastStartedAt.toLocaleString(locale) })
            : undefined
        }
      >
        {label}
      </span>
      {manualStartRejected && <span className="text-muted-foreground">{rejectedLabel}</span>}
      {statusQuery.isError ? (
        <Button
          variant="link"
          size="sm"
          className="px-0"
          onClick={() => void statusQuery.refetch()}
        >
          {t("common.retry")}
        </Button>
      ) : (
        <Button
          variant="icon"
          size="icon"
          className={cn(
            "relative rounded-(--icon-button-radius) text-primary hover:text-primary",
            // Keep the cooldown ring readable while the button waits.
            waitMs > 0 && "disabled:opacity-100",
          )}
          disabled={!canRefresh}
          onClick={() => start.mutate()}
          aria-label={
            waitMs > 0 ? tn("sync.availableInMinutes", waitMinutes) : t("sync.refreshAccounts")
          }
          title={
            waitMs > 0
              ? t("sync.nextRefresh", {
                  time: new Date(status!.next_allowed_manual_sync_at!).toLocaleString(locale),
                })
              : t("sync.refreshNow")
          }
        >
          {waitMs > 0 ? (
            <>
              {/* Cooldown ring: fills up as the next manual refresh gets closer. */}
              <svg viewBox="0 0 36 36" className="absolute inset-0 size-full" aria-hidden="true">
                <circle
                  cx="18"
                  cy="18"
                  r="15"
                  fill="none"
                  stroke="currentColor"
                  strokeOpacity="0.15"
                  strokeWidth="3"
                />
                <circle
                  cx="18"
                  cy="18"
                  r="15"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeDasharray={30 * Math.PI}
                  strokeDashoffset={30 * Math.PI * (1 - cooldownProgress)}
                  transform="rotate(-90 18 18)"
                />
              </svg>
              <span className="text-[calc(var(--text-xs)*5/6)] font-label tabular-nums">
                {t("sync.minutesShort", { minutes: formatNumber(waitMinutes) })}
              </span>
            </>
          ) : (
            <RotateCwIcon aria-hidden="true" />
          )}
        </Button>
      )}
      {startError && <span className="text-destructive">{t("sync.couldNotConfirm")}</span>}
    </div>
  );
}
